import React from "react";
import { GiftHighlight } from "@/components/MiniGiftBox";
import { Link } from "react-router-dom";
import { ArrowRight, Package } from "lucide-react";
import { PICK_YOUR_SET_BOXES } from "@/data/products";

/**
 * PickYourSetSection — "Pick Your Perfect Set ✦"
 * Two SQUARE editorial cards side-by-side using shared PICK_YOUR_SET_BOXES data.
 */

const SETS = PICK_YOUR_SET_BOXES;

const SetCard = ({ set }) => {
  const t = set.theme;
  return (
  <Link
    to={set.to}
    className={`group relative flex-1 flex flex-col rounded-[26px] overflow-hidden shadow-[0_22px_44px_-24px_rgba(74,44,53,0.32)] hover:shadow-[0_28px_58px_-24px_rgba(74,44,53,0.48)] transition-shadow ${t.dark ? "ring-1 ring-gold/50" : ""}`}
    style={{ background: t.bg, border: `1px solid ${t.border}` }}
    data-testid={`pick-your-set-${set.id}`}
  >
    {/* Square media — video if provided, else image. Ribbon overlays inside so
        both cards keep identical structure (perfect alignment). */}
    <div className="relative aspect-square overflow-hidden">
      {set.video ? (
        <video
          src={set.video}
          poster={set.image}
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-[900ms] group-hover:scale-[1.05]"
          data-testid={`pick-your-set-${set.id}-video`}
        />
      ) : (
        <img
          src={set.image}
          alt={set.label}
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-[900ms] group-hover:scale-[1.05]"
          loading="lazy"
          draggable="false"
        />
      )}

    </div>

    {/* Meta */}
    <div className="relative p-5 pt-6 lg:p-7 lg:pt-8 flex flex-col items-center text-center gap-2.5">
      {/* Ribbon sits on the image/meta seam — never over faces */}
      {set.ribbon && (
        <span
          className="absolute -top-3.5 left-1/2 -translate-x-1/2 whitespace-nowrap inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] tracking-[0.28em] uppercase font-semibold text-plum shadow-md"
          style={{
            background:
              "linear-gradient(135deg, #F4D58D 0%, #E0BC76 55%, #C5985A 100%)",
          }}
          data-testid={`pick-your-set-${set.id}-ribbon`}
        >
          {set.ribbon}
        </span>
      )}
      <p
        className="text-[11px] lg:text-[12px] tracking-[0.32em] uppercase font-semibold"
        style={{ color: t.accent }}
      >
        {set.label}
      </p>
      <div className="flex items-baseline gap-1.5">
        <span
          className="font-serif leading-none"
          style={{ fontSize: "clamp(30px, 3.6vw, 44px)", color: t.text }}
        >
          {set.price}
        </span>
        <span className="text-[11px] tracking-[0.18em] uppercase opacity-60" style={{ color: t.text }}>
          only
        </span>
      </div>

      {/* Offer pill */}
      <span
        className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] lg:text-[11px] tracking-[0.24em] uppercase font-semibold mt-1"
        style={{ background: t.pill, border: `1px solid ${t.border}`, color: t.dark ? t.accent : t.text }}
      >
        <Package size={10} strokeWidth={2.4} />
        {set.pieces}
      </span>
      {set.gift && <GiftHighlight text={set.gift} testId="pick-your-set-dream-gift" />}

      {/* Category line */}
      <p className="text-[13px] lg:text-sm mt-1.5 font-serif opacity-75" style={{ color: t.text }}>
        Earrings · Necklaces · Rings —{" "}
        <span className="italic">your pick</span>
      </p>

      {/* CTA */}
      <span
        className="inline-flex items-center gap-1.5 mt-3 text-[11px] lg:text-[12px] tracking-[0.28em] uppercase font-semibold transition-colors"
        style={{ color: t.dark ? t.accent : t.text }}
      >
        Build Your Box
        <ArrowRight
          size={14}
          strokeWidth={2.4}
          className="transition-transform group-hover:translate-x-0.5"
        />
      </span>
    </div>
  </Link>
  );
};

const PickYourSetSection = () => (
  <section
    id="pick-your-set"
    className="relative py-12 lg:py-20 overflow-hidden"
    style={{ background: "linear-gradient(180deg, #FDF6F0 0%, #FBEAF0 100%)" }}
    data-testid="pick-your-set-section"
  >
    <div className="max-w-[1120px] mx-auto px-5 lg:px-10">
      {/* Header */}
      <div className="text-center mb-9 lg:mb-12">
        <p
          className="text-[10px] lg:text-[11px] tracking-[0.42em] uppercase font-semibold mb-3 flex items-center justify-center gap-3"
          style={{ color: "#C5985A" }}
        >
          <span className="h-px w-8 bg-gold/50" />
          Build a Box
          <span className="h-px w-8 bg-gold/50" />
        </p>
        <h2
          className="font-serif text-plum leading-[1.05] inline-flex items-center gap-2"
          style={{ fontSize: "clamp(28px, 4.2vw, 50px)" }}
        >
          Pick Your Perfect Set{" "}
          <span className="text-gold" aria-hidden>
            ✦
          </span>
        </h2>
        <p className="mt-2 text-plum/65 text-sm lg:text-base font-serif italic">
          Premium anti-tarnish jewels — mix &amp; match freely
        </p>
      </div>

      {/* Cards */}
      <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 lg:gap-8">
        {SETS.map((s) => (
          <SetCard key={s.id} set={s} />
        ))}
      </div>

      {/* Footer badge line */}
      <p
        className="mt-8 lg:mt-10 text-center text-[10px] lg:text-[11px] tracking-[0.32em] uppercase font-semibold text-plum/60 flex items-center justify-center gap-2"
        data-testid="pick-your-set-badges"
      >
        <span className="text-gold" aria-hidden>✦</span>
        Free Shipping · COD Available · Easy Returns
        <span className="text-gold" aria-hidden>✦</span>
      </p>
    </div>
  </section>
);

export default PickYourSetSection;
